import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Stack,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SubPageLayout from '../../components/SubPageLayout';
import { passwordRulesPass, PasswordChecklist } from '../../components/PasswordRules';

const ForgotCurrentPasswordCard = ({ email, sending, sent, error, onSendLink, t }) => (
  <Card>
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: (theme) => theme.alpha((theme.vars || theme).palette.primary.main, 0.08), color: 'primary.main' }}>
          <MailOutlineIcon />
        </Avatar>
      }
      title={t('changePassword.forgotTitle')}
      subheader={t('changePassword.forgotSubtitle')}
      titleTypographyProps={{ variant: 'h6' }}
      subheaderTypographyProps={{ variant: 'body2' }}
    />
    <Divider />
    <CardContent>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="overline" color="text.secondary">
              {t('changePassword.forgotSendTo')}
            </Typography>
            <Typography variant="body1">{email}</Typography>
          </Box>
          <Button
            variant="outlined"
            disabled={sent || sending || !email}
            onClick={onSendLink}
          >
            {sent ? t('changePassword.forgotSentButton') : t('changePassword.forgotSendButton')}
          </Button>
        </Stack>

        {sent && (
          <Alert severity="info">
            {t('changePassword.forgotSentInfo', { email })}
          </Alert>
        )}
        {error && (
          <Alert severity="error">{t('changePassword.forgotError')}</Alert>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { collaborator } = useAuth();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew1, setShowNew1] = useState(false);
  const [showNew2, setShowNew2] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const allRulesPass = useMemo(() => passwordRulesPass(newPassword1), [newPassword1]);

  const mutation = useMutation({
    mutationFn: () => authService.changePassword(oldPassword, newPassword1, newPassword2),
    onSuccess: () => {
      setSuccess(true);
      setOldPassword('');
      setNewPassword1('');
      setNewPassword2('');
      setFieldErrors({});
    },
    onError: (error) => {
      const data = error?.response?.data;
      if (data && typeof data === 'object') {
        setFieldErrors(data);
      } else {
        setFieldErrors({ non_field_errors: [t('changePassword.errorGeneric')] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(false);
    setFieldErrors({});
    mutation.mutate();
  };

  const handleClear = () => {
    setOldPassword('');
    setNewPassword1('');
    setNewPassword2('');
  };

  const firstError = (key) => fieldErrors[key]?.[0] || null;

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const mismatch = newPassword2.length > 0 && newPassword2 !== newPassword1;

  const forgotPasswordMutation = useMutation({
    mutationFn: () => authService.passwordReset(collaborator?.email),
  });

  return (
    <SubPageLayout title={t('changePassword.title')} onBack={() => navigate(location.state?.from || '/settings')}>
      <Stack spacing={3}>
        <Card component="form" onSubmit={handleSubmit}>
          <CardHeader
            title={t('changePassword.title')}
            subheader={t('changePassword.subtitle')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              {success && (
                <Alert severity="success">{t('changePassword.success')}</Alert>
              )}

              {firstError('non_field_errors') && (
                <Alert severity="error">{firstError('non_field_errors')}</Alert>
              )}

              <TextField
                fullWidth
                type={showOld ? 'text' : 'password'}
                label={t('changePassword.oldPassword')}
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  clearFieldError('old_password');
                }}
                error={!!firstError('old_password')}
                helperText={firstError('old_password') || t('changePassword.oldPasswordHint')}
                disabled={mutation.isPending}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowOld((v) => !v)} edge="end">
                        {showOld ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box>
                <TextField
                  fullWidth
                  type={showNew1 ? 'text' : 'password'}
                  label={t('changePassword.newPassword')}
                  value={newPassword1}
                  onChange={(e) => {
                    setNewPassword1(e.target.value);
                    clearFieldError('new_password1');
                  }}
                  error={!!firstError('new_password1')}
                  helperText={firstError('new_password1')}
                  disabled={mutation.isPending}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowNew1((v) => !v)} edge="end">
                          {showNew1 ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <PasswordChecklist password={newPassword1} t={t} />
              </Box>
              <TextField
                fullWidth
                type={showNew2 ? 'text' : 'password'}
                label={t('changePassword.confirmPassword')}
                value={newPassword2}
                onChange={(e) => {
                  setNewPassword2(e.target.value);
                  clearFieldError('new_password2');
                }}
                error={!!firstError('new_password2') || mismatch}
                helperText={
                  firstError('new_password2')
                    || (mismatch ? t('changePassword.mismatch') : t('changePassword.confirmPasswordHint'))
                }
                disabled={mutation.isPending}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNew2((v) => !v)} edge="end">
                        {showNew2 ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button color="primary" onClick={handleClear} disabled={mutation.isPending}>
              {t('changePassword.clear')}
            </Button>
            <Button
              type="submit"
              variant="outlined"
              disabled={mutation.isPending || !oldPassword || !allRulesPass || !newPassword2 || mismatch}
            >
              {mutation.isPending
                ? <CircularProgress size={24} color="inherit" />
                : t('changePassword.submit')}
            </Button>
          </CardActions>
        </Card>

        <ForgotCurrentPasswordCard
          email={collaborator?.email}
          sending={forgotPasswordMutation.isPending}
          sent={forgotPasswordMutation.isSuccess}
          error={forgotPasswordMutation.isError}
          onSendLink={() => forgotPasswordMutation.mutate()}
          t={t}
        />
      </Stack>
    </SubPageLayout>
  );
};

export default ChangePassword;
